import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 * Handles the #session_id= fragment returned by Emergent-managed Google OAuth.
 * Exchanges the session_id for a CoachIQ JWT via POST /api/auth/google/callback, then
 * stores the token like a normal login and lands the coach on /dashboard.
 */
export default function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { setSessionFromToken } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { nav("/login", { replace: true }); return; }
    const session_id = decodeURIComponent(m[1]);
    (async () => {
      try {
        const { data } = await api.post("/auth/google/callback", { session_id });
        localStorage.setItem("coachiq_token", data.token);
        await setSessionFromToken(data.token, data.user);
        const dest = data.user?.needs_consent ? "/consent" : "/dashboard";
        window.history.replaceState(null, "", dest);
        toast.success(`Welcome, ${data.user.name.split(" ")[0]}`);
        nav(dest, { replace: true });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Google sign-in failed");
        nav("/login", { replace: true });
      }
    })();
  }, [location.hash, nav, setSessionFromToken]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white" data-testid="auth-callback">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto rounded-2xl bg-[#007AFF] flex items-center justify-center animate-pulse">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div className="mt-4 text-sm text-gray-600">Signing you in…</div>
      </div>
    </div>
  );
}
